import * as Flags from 'country-flag-icons/react/1x1';
import { forwardRef, useId, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Country data: [iso2, dialCode, format pattern]
// Format: # = digit, space = space
const countryData = [
  ['CZ', '+420', '### ### ###'],
  ['SK', '+421', '### ### ###'],
  ['AT', '+43', '### ### ####'],
  ['BE', '+32', '### ## ## ##'],
  ['BG', '+359', '## ### ####'],
  ['HR', '+385', '## ### ####'],
  ['CY', '+357', '## ### ###'],
  ['DK', '+45', '## ## ## ##'],
  ['EE', '+372', '#### ####'],
  ['FI', '+358', '## ### ## ##'],
  ['FR', '+33', '# ## ## ## ##'],
  ['DE', '+49', '### ### ####'],
  ['GR', '+30', '### ### ####'],
  ['HU', '+36', '## ### ####'],
  ['IE', '+353', '## ### ####'],
  ['IT', '+39', '### ### ####'],
  ['LV', '+371', '## ### ###'],
  ['LT', '+370', '### ## ###'],
  ['LU', '+352', '### ### ###'],
  ['MT', '+356', '#### ####'],
  ['NL', '+31', '# ## ## ## ##'],
  ['PL', '+48', '### ### ###'],
  ['PT', '+351', '### ### ###'],
  ['RO', '+40', '### ### ###'],
  ['SI', '+386', '## ### ###'],
  ['ES', '+34', '### ### ###'],
  ['SE', '+46', '## ### ## ##'],
  ['CH', '+41', '## ### ## ##'],
  ['GB', '+44', '#### ### ###'],
  ['UA', '+380', '## ### ## ##'],
] as const;

type CountryCode = (typeof countryData)[number][0];

// Get localized country name using Intl.DisplayNames
const getCountryName = (iso2: string, locale: string): string => {
  try {
    const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
    return displayNames.of(iso2) || iso2;
  } catch {
    return iso2;
  }
};

// Map locale to default country code
const localeToCountry: Record<string, CountryCode> = {
  cs: 'CZ',
  sk: 'SK',
  de: 'DE',
  en: 'GB',
  pl: 'PL',
  hu: 'HU',
  fr: 'FR',
  it: 'IT',
  es: 'ES',
  nl: 'NL',
  pt: 'PT',
  ro: 'RO',
  bg: 'BG',
  hr: 'HR',
  sl: 'SI',
  et: 'EE',
  lv: 'LV',
  lt: 'LT',
  fi: 'FI',
  sv: 'SE',
  da: 'DK',
  el: 'GR',
  uk: 'UA',
};

const getFlag = (code: string) => {
  return Flags[code as keyof typeof Flags] || null;
};

// Get max digits from pattern
const getMaxDigits = (pattern: string): number => {
  return pattern.split('').filter((c) => c === '#').length;
};

// Format phone number according to pattern
const formatPhoneNumber = (digits: string, pattern: string): string => {
  const maxDigits = getMaxDigits(pattern);
  const limitedDigits = digits.slice(0, maxDigits);

  let result = '';
  let digitIndex = 0;

  for (const char of pattern) {
    if (digitIndex >= limitedDigits.length) break;

    if (char === '#') {
      result += limitedDigits[digitIndex];
      digitIndex++;
    } else {
      result += char;
    }
  }

  return result;
};

// Extract only digits from string
const extractDigits = (value: string): string => {
  return value.replace(/\D/g, '');
};

export interface PhoneInputProps {
  value: string;
  onChange: (_value: string) => void;
  onBlur?: () => void;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  defaultCountry?: CountryCode;
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value, onChange, onBlur, label, error, required, disabled, id, className = '', defaultCountry }, ref) => {
    const { i18n } = useTranslation();
    const locale = i18n.language;
    const generatedId = useId();
    const inputId = id || generatedId;
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Determine default country from prop or locale
    const resolvedDefaultCountry = defaultCountry || localeToCountry[locale] || 'CZ';

    const [isOpen, setIsOpen] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState<CountryCode>(resolvedDefaultCountry);

    const currentCountry = countryData.find((c) => c[0] === selectedCountry) || countryData[0];
    const CurrentFlag = getFlag(selectedCountry);

    // Parse phone number to extract local number (without dial code)
    const getLocalNumber = (phone: string): string => {
      // Remove all non-digits first to check dial code
      const digits = extractDigits(phone);
      const dialCodeDigits = extractDigits(currentCountry[1]);

      if (digits.startsWith(dialCodeDigits)) {
        return digits.slice(dialCodeDigits.length);
      }

      // Check if starts with any dial code
      for (const [, code] of countryData) {
        const codeDigits = extractDigits(code);
        if (digits.startsWith(codeDigits)) {
          return digits.slice(codeDigits.length);
        }
      }

      return digits;
    };

    const localDigits = getLocalNumber(value);
    const formattedLocal = formatPhoneNumber(localDigits, currentCountry[2]);

    const handleCountrySelect = (country: (typeof countryData)[number]) => {
      setSelectedCountry(country[0]);
      setIsOpen(false);
      // Update value with new dial code, or empty if no digits
      const newValue = localDigits.length > 0 ? country[1] + localDigits : '';
      onChange(newValue);
      buttonRef.current?.focus();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const digits = extractDigits(inputValue);
      const maxDigits = getMaxDigits(currentCountry[2]);
      const limitedDigits = digits.slice(0, maxDigits);
      // Return empty string if no digits, otherwise full number with dial code
      const fullNumber = limitedDigits.length > 0 ? currentCountry[1] + limitedDigits : '';
      onChange(fullNumber);
    };

    // Close dropdown on outside click
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    return (
      <div className={`phone-input-wrapper ${className}`} data-required={required}>
        {label && (
          <label htmlFor={inputId} className="form-label">
            {label}
          </label>
        )}
        <div className="phone-input-container" ref={dropdownRef}>
          <button
            ref={buttonRef}
            type="button"
            className={`phone-input-country-btn ${error ? 'is-invalid' : ''}`}
            onClick={() => setIsOpen(!isOpen)}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            {CurrentFlag && <CurrentFlag className="phone-input-flag" />}
            <span className="phone-input-dial-code">{currentCountry[1]}</span>
            <svg className="phone-input-arrow" width="10" height="10" viewBox="0 0 16 16" fill="none">
              <path
                d="M2 5L8 11L14 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <input
            ref={ref}
            id={inputId}
            type="tel"
            className={`form-control phone-input-field ${error ? 'is-invalid' : ''}`}
            value={formattedLocal}
            onChange={handleInputChange}
            onBlur={onBlur}
            disabled={disabled}
            required={required}
            autoComplete="tel-national"
            placeholder={currentCountry[2].replace(/#/g, '0')}
          />

          {isOpen && (
            <div className="phone-input-dropdown" role="listbox" onKeyDown={handleKeyDown}>
              {countryData.map((country) => {
                const Flag = getFlag(country[0]);
                const isSelected = country[0] === selectedCountry;
                return (
                  <button
                    key={country[0]}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`phone-input-dropdown-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleCountrySelect(country)}
                  >
                    {Flag && <Flag className="phone-input-flag" />}
                    <span className="phone-input-country-name">{getCountryName(country[0], locale)}</span>
                    <span className="phone-input-country-code">{country[1]}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {error && <div className="invalid-feedback d-block">{error}</div>}
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
